import { Suspense, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { MetaTags } from "@Constant/constants";
import { Helmet } from "react-helmet";
import {
  BrowserRouter as Router,
  Navigate as Redirect,
  Route,
  Routes as Switch,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import MainLayout from "@Layouts/MainLayout";
import AuthLayout from "@Layouts/AuthLayout";
import UserLayout from "@Layouts/UserLayout";

import { privateRoutes, publicRoutes } from "./routeConstant";
import { useSelector } from "react-redux";
import { ShowContent } from "../Utils/roleAndPermission";
import Loader from "../Components/Loader";

const RouteWithRedirect = () => {
  const [searchParams] = useSearchParams();

  const reduxToken = useSelector((state) => state?.authSlice?.user?.token) || "";
  const accessToken = reduxToken || localStorage.getItem("token") || "";

  const permissionsForRole =
    useSelector((state) => state?.authSlice?.user?.user?.permissions) || [];

  const redirectPath = searchParams.get("redirect");

  return (
    <Suspense fallback={<Loader />}>
      <Switch>
        {!accessToken ? (
          <>
            {publicRoutes.map(({ path, element: Element }, index) => (
              <Route
                key={index}
                path={path}
                element={
                  <div id="app">
                    <div className="content-wrapper">
                      <AuthLayout>
                        <Element />
                      </AuthLayout>
                    </div>
                  </div>
                }
              />
            ))}
            <Route
              path="/"
              element={
                <AuthLayout>
                  <Redirect to="/login" replace />
                </AuthLayout>
              }
            />
            <Route
              path="*"
              element={
                <AuthLayout>
                  <Redirect to="/login" replace />
                </AuthLayout>
              }
            />
          </>
        ) : (
          <>
            <Route
              path="/login"
              element={<Redirect to={redirectPath || "/dashboard"} replace />}
            />
            <Route
              path="/signup"
              element={<Redirect to={redirectPath || "/dashboard"} replace />}
            />
            <Route
              path="/forgot-password"
              element={<Redirect to={redirectPath || "/dashboard"} replace />}
            />
            <Route
              path="/reset-password"
              element={<Redirect to={redirectPath || "/dashboard"} replace />}
            />
            <Route
              path="/set-new-password"
              element={<Redirect to={redirectPath || "/dashboard"} replace />}
            />
            <Route path="/" element={<Redirect to="/dashboard" replace />} />
          </>
        )}
        {accessToken && (
          <>
            {privateRoutes.map(
              ({ path, element: Element, roles }, index) =>
                [...permissionsForRole, "all"]?.includes(roles) && (
                  <Route
                    key={index}
                    path={path}
                    element={
                      <div id="app">
                        <div>
                          <UserLayout>
                            <Element />
                          </UserLayout>
                        </div>
                      </div>
                    }
                  />
                )
            )}
            <Route
              path="*"
              element={
                <UserLayout>
                  <ShowContent permission="dashboard">
                    <Redirect to="/dashboard" replace />
                  </ShowContent>
                </UserLayout>
              }
            />
          </>
        )}
      </Switch>
    </Suspense>
  );
};

export default function RootRoutes() {
  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: "",
          duration: 5000,
          style: {
            background: "#ffffff",
            color: "black",
            fontWeight: "600",
            fontSize: "14px",
            textAlign: "left",
          },

          success: {
            duration: 3000,
            style: {
              borderLeft: "10px solid #61D345",
            },
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
          error: {
            duration: 3000,
            style: {
              borderLeft: "4px solid red",
            },
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
          loading: {
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
        }}
      />
      <MainLayout>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <LocationWrapper />
          <RouteWithRedirect />
        </Router>
      </MainLayout>
    </>
  );
}

const LocationWrapper = () => {
  const { pathname } = useLocation();
  const currentMeta =
    MetaTags.find((item) => item.path === pathname.replace(/\/\d+/g, "")) ||
    MetaTags[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      {currentMeta && (
        <Helmet>
          <title>{currentMeta?.title}</title>
          <meta name="description" content={currentMeta?.description} />
          <meta name="path" content={currentMeta?.viewPath} />
        </Helmet>
      )}
    </>
  );
};
